/**
 * App version metadata, baked in at build time.
 *
 * Vite replaces the `import.meta.env.VITE_APP_VERSION_*` references via the
 * `define` plugin (see vite.config.ts). In CI we get the SHA from
 * `${{ github.sha }}` / `${{ github.ref_name }}`; locally Vite shells out to
 * `git rev-parse HEAD` so the SHA in the console actually reflects the
 * working tree.
 */
export interface AppVersion {
  /** Short commit SHA (first 7 chars), or `'unknown'` when no git context. */
  sha: string;
  /** Branch / tag name, or `'unknown'`. */
  ref: string;
  /** ISO-8601 build timestamp. */
  buildTime: string;
  /** Vite mode: `'development'`, `'production'`, … */
  mode: string;
}

const env = import.meta.env;

// Vite always builds with mode='production' in CI (one build artifact is
// promoted across stages), so deriving the deployment from the runtime
// hostname is more useful in the version banner than the static Vite mode.
export function deriveDeployment(viteMode: string): string {
  if (typeof window === 'undefined') return viteMode;
  const host = window.location.hostname;
  if (host === 'staging.notifi.co' || host === 'www.staging.notifi.co') return 'staging';
  if (host === 'app.notifi.co' || host === 'www.app.notifi.co') return 'production';
  if (host === 'localhost' || host.endsWith('.localhost') || host === '127.0.0.1')
    return 'development';
  return viteMode;
}

export const APP_VERSION: AppVersion = {
  sha: env.VITE_APP_VERSION_SHA ?? 'unknown',
  ref: env.VITE_APP_VERSION_REF ?? 'unknown',
  buildTime: env.VITE_APP_VERSION_BUILD_TIME ?? new Date(0).toISOString(),
  mode: deriveDeployment(env.VITE_APP_VERSION_MODE ?? 'unknown'),
};

/**
 * Print the version banner and expose it on `window.__NOTIFI_VERSION__` so
 * support / smoke scripts can read it from the console without scrolling
 * the log.
 */
export function logVersion(): void {
  // eslint-disable-next-line no-console
  console.log(
    `%cNotifi · ${APP_VERSION.sha} · ${APP_VERSION.ref} · ${APP_VERSION.buildTime} · ${APP_VERSION.mode}`,
    'font-weight:600',
  );
  const slot = globalThis as unknown as { __NOTIFI_VERSION__?: AppVersion };
  slot.__NOTIFI_VERSION__ = APP_VERSION;
}
